import React from "react";
import { BottomSheet } from "@/commons/BottomSheet";
import { FaDumbbell, FaUserShield, FaClipboardCheck, FaMapMarkerAlt } from "react-icons/fa";
import { MdOutlineSupportAgent, MdOutlinePhoneAndroid, MdOutlineAssessment } from "react-icons/md";
import { GiMeal, GiProgression } from "react-icons/gi";
import { BsGraphUp, BsCalendarCheck } from "react-icons/bs";

// Wording that differs outside Singapore (GymPod, condo facilities and the
// Essential tier are Singapore-only). market: UAE / Malaysia; SG passes none.
const MARKET_COPY = {
  ae: {
    gymFees: "If you wish to train at a commercial gym, the trainer's entry fee is borne by the client.",
    plan: "Location-based training plans (home, building or compound gym, or outdoor)",
    certified: "Work with qualified trainers holding internationally recognised certifications (e.g. ACE, NASM, REPs UAE)",
    tiers: "Choose Signature (experienced) or Elite (expert) trainers",
    where: "Training at your villa or apartment, your building or compound gym, or a local gym",
  },
  my: {
    gymFees: "If you wish to train at a commercial gym, the trainer's entry fee is borne by the client.",
    plan: "Location-based training plans (home, condo gym, or outdoor)",
    certified: "Work with qualified trainers holding recognised certifications (e.g. ACE, NASM)",
    tiers: "Choose Signature (experienced) or Elite (expert) trainers",
    where: "Training at your home, condo gym, or a local gym",
  },
}

export const PersonalTrainerServiceDetails = ({ setIsShowServiceDetails, market }) => {
  const copy = market ? MARKET_COPY[market.countryCd] : null
  const isOpen = true;

  const handleClose = () => {
    setIsShowServiceDetails(false);
  };

  return (
    <BottomSheet 
      isOpen={isOpen} 
      onClose={handleClose}
      maxWidth="2xl"
    >
      {/* Header */}
      <BottomSheet.Header>
        <BottomSheet.Title>Training Package Details</BottomSheet.Title>
        <BottomSheet.Subtitle>What&apos;s included in your fitness journey</BottomSheet.Subtitle>
      </BottomSheet.Header>

      {/* Content */}
      <BottomSheet.Content className="space-y-6 text-gray-700 text-sm leading-6">
        {/* Intro */}
        <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
          <p className="text-gray-600">
            Each session is <span className="font-semibold text-orange-600">45 minutes to 1 hour</span> long. 
            {copy ? copy.gymFees : <>If you wish to train at GymPod or public gyms, the trainer&apos;s entry fees are to be borne by the client.</>}
          </p>
        </div>

        {/* Assessment & Planning Section */}
        <ServiceSection 
          icon={<MdOutlineAssessment className="text-xl" />}
          title="Comprehensive Assessment & Planning"
        >
          <ServiceItem 
            icon={<MdOutlineAssessment />}
            title="Fitness & Health Assessment"
            items={[
              "Detailed consultation discussing your fitness goals, medical history, and lifestyle",
              "Comprehensive movement analysis covering strength, cardiovascular capacity, flexibility, and posture",
              "Body composition assessment and baseline fitness measurements",
              "Goal setting and realistic timeline establishment"
            ]}
          />
          <ServiceItem 
            icon={<FaClipboardCheck />}
            title="Personalized Training & Nutrition Plan"
            items={[
              "Custom workout programs designed for your specific goals and fitness level",
              copy ? copy.plan : "Location-based training plans (home, condo gym, community facilities, or outdoor)",
              "Detailed nutrition guidelines aligned with your dietary preferences and restrictions",
              "Calorie and macronutrient targets with meal timing recommendations"
            ]}
          />
        </ServiceSection>

        {/* Training Sessions Section */}
        <ServiceSection 
          icon={<FaDumbbell className="text-xl" />}
          title="Professional Training Sessions"
        >
          <ServiceItem 
            icon={<FaDumbbell />}
            title="Certified Personal Trainer Guidance"
            items={[
              copy ? copy.certified : "Work with qualified trainers certified by ACE, NASM, or Singapore-recognized programs",
              copy ? copy.tiers : "Choose from Essential (entry-level), Signature (experienced), or Elite (expert) trainers",
              "2-3 sessions per week with consistent progress monitoring",
              "Real-time form correction and technique guidance"
            ]}
          />
          <ServiceItem 
            icon={<FaUserShield />}
            title="Safe & Effective Training Environment"
            items={[
              "Injury prevention techniques and proper exercise execution",
              "Progressive overload programming for continuous improvement",
              "Motivation, accountability, and goal-focused training approach",
              "Equipment usage guidance and safety protocols"
            ]}
          />
        </ServiceSection>

        {/* Nutrition & Diet Section */}
        <ServiceSection 
          icon={<GiMeal className="text-xl" />}
          title="Nutrition & Diet Integration"
        >
          <ServiceItem 
            icon={<GiMeal />}
            title="Customized Nutrition Program"
            items={[
              "Personalized meal plans based on your goals (weight loss, muscle gain, maintenance)",
              "Dietary preference accommodation (plant-based, halal, vegetarian, etc.)",
              "Calorie tracking support and portion control guidance",
              "Hydration recommendations and supplement guidance"
            ]}
          />
          <ServiceItem 
            icon={<BsGraphUp />}
            title="Nutrition Education & Support"
            items={[
              "Understanding macronutrients and their role in your fitness goals",
              "Meal prep strategies and grocery shopping guidance",
              "Restaurant dining choices and eating out strategies",
              "Ongoing nutrition adjustments based on progress and feedback"
            ]}
          />
        </ServiceSection>

        {/* Progress Tracking & Support */}
        <ServiceSection 
          icon={<GiProgression className="text-xl" />}
          title="Progress Monitoring & Ongoing Support"
        >
          <ServiceItem 
            icon={<GiProgression />}
            title="Regular Progress Assessments"
            items={[
              "Weekly/bi-weekly check-ins to monitor progress and make adjustments",
              "Body measurements, performance metrics, and milestone tracking",
              "Program modifications based on your development and feedback",
              "Motivation and accountability throughout your fitness journey"
            ]}
          />
          <ServiceItem 
            icon={<MdOutlineSupportAgent />}
            title="24/7 Support & Communication"
            items={[
              "Direct communication with your trainer via app or messaging",
              "Emergency support and exercise modifications when needed",
              "Access to fitness resources and educational content",
              "Community support and success story inspiration"
            ]}
          />
        </ServiceSection>

        {/* Technology & Convenience */}
        <ServiceSection 
          icon={<MdOutlinePhoneAndroid className="text-xl" />}
          title="Technology & Convenience Features"
        >
          <ServiceItem 
            icon={<MdOutlinePhoneAndroid />}
            title="StringsSG Mobile App Integration"
            items={[
              "Easy booking and rescheduling through the mobile app",
              "Direct messaging with your personal trainer",
              "Progress photo uploads and workout logging",
              "Access to personalized diet plans and meal tracking"
            ]}
          />
          <ServiceItem 
            icon={<FaMapMarkerAlt />}
            title="Flexible Location Options"
            items={[
              copy ? copy.where : "Training at your home, condo facilities, or local gyms",
              "Outdoor training sessions in parks and recreational areas",
              "Concierge service for equipment transportation if needed",
              "Virtual training options for remote coaching"
            ]}
          />
          <ServiceItem 
            icon={<BsCalendarCheck />}
            title="Scheduling Flexibility"
            items={[
              "Choose session times that fit your schedule (weekdays, weekends, evenings)",
              "Easy rescheduling with 24-hour notice",
              "Make-up sessions for missed appointments",
              "Package validity periods to accommodate your timeline"
            ]}
          />
        </ServiceSection>

        {/* Value Proposition */}
        <div className="bg-linear-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-4 space-y-4">
          <h5 className="font-bold text-orange-800">Why Choose StringsSG Personal Training?</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <p className="font-semibold text-orange-700">Quality Assurance:</p>
              <ul className="space-y-1 text-gray-600">
                <li>• Vetted, certified trainers with proven track records</li>
                <li>• Comprehensive insurance coverage for peace of mind</li>
                <li>• Transparent pricing with no hidden fees</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-orange-700">Proven Results:</p>
              <ul className="space-y-1 text-gray-600">
                <li>• 6-step scientifically-backed training methodology</li>
                <li>• Thousands of successful client transformations</li>
                <li>• Sustainable lifestyle changes for long-term success</li>
              </ul>
            </div>
          </div>
        </div>
      </BottomSheet.Content>

      {/* Footer */}
      <BottomSheet.Footer className="flex gap-3">
        <BottomSheet.CloseButton variant="primary">
          Got it!
        </BottomSheet.CloseButton>
      </BottomSheet.Footer>
    </BottomSheet>
  );
};

// Service Section Component
const ServiceSection = ({ icon, title, children }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-3 pb-2 border-b border-orange-200">
      <div className="flex items-center justify-center w-8 h-8 bg-orange-100 text-orange-600 rounded-lg">
        {icon}
      </div>
      <h4 className="text-base font-bold text-orange-600">{title}</h4>
    </div>
    <div className="space-y-4">
      {children}
    </div>
  </div>
);

// Service Item Component
const ServiceItem = ({ icon, title, items }) => (
  <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-4">
    <div className="flex items-center justify-center w-8 h-8 bg-white text-orange-500 rounded-lg shadow-sm shrink-0 mt-0.5">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-gray-800 mb-2">{title}</p>
      <ul className="space-y-1.5">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
            <span className="text-orange-400 mt-1.5 text-[6px]">●</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);
